import { useAdminTransactions, type TransactionStatut } from '@fidalia/api';
import { Alert, DataTable, Input, PageHeader, Pagination, Select } from '@fidalia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { MdCalendarToday, MdFilterList, MdReceiptLong, MdSearch } from 'react-icons/md';
import { useState } from 'react';

import { STATUTS, colonnesTransactions, formatEuros } from '@/features/billing/transaction-columns';
import { errorMessage } from '@/lib/auth';
import { useDebounced } from '@/lib/use-debounced';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_app/subscriptions')({
  component: AbonnementsPage,
});

/** Profondeurs d'historique proposées, en mois. */
const FENETRES = [
  { valeur: 3, label: '3 derniers mois' },
  { valeur: 12, label: '12 derniers mois' },
  { valeur: 24, label: '2 dernières années' },
  { valeur: 60, label: 'Tout l’historique' },
] as const;

const columns = colonnesTransactions({ avecCabinet: true });

function AbonnementsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statut, setStatut] = useState<TransactionStatut | ''>('');
  const [mois, setMois] = useState(12);
  const debouncedSearch = useDebounced(search, 300);

  /** Tout changement de filtre repart de la première page. */
  const filtrer =
    <T,>(setter: (valeur: T) => void) =>
    (valeur: T) => {
      setter(valeur);
      setPage(1);
    };

  const { data, isPending, error } = useAdminTransactions({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch,
    statut,
    mois,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdReceiptLong />}
        title="Abonnements"
        description="Toutes les transactions Stripe, avec le cabinet et la formule facturée."
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}

      {data?.tronque ? (
        <Alert tone="info" title="Historique tronqué">
          La fenêtre sélectionnée dépasse le plafond de lecture Stripe. Réduisez la période pour
          voir des totaux exacts.
        </Alert>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          type="search"
          aria-label="Rechercher une transaction"
          placeholder="Cabinet, responsable, n° de facture…"
          icon={<MdSearch className="size-6" />}
          containerClassName="h-11 w-full sm:min-w-64 sm:flex-1"
          value={search}
          onChange={(event) => filtrer(setSearch)(event.target.value)}
        />

        <Select
          aria-label="Filtrer par statut"
          icon={<MdFilterList className="size-5" />}
          containerClassName="h-11 w-full sm:w-56"
          value={statut}
          onChange={(event) => filtrer(setStatut)(event.target.value as TransactionStatut | '')}
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUTS).map(([valeur, { label }]) => (
            <option key={valeur} value={valeur}>
              {label}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Profondeur d'historique"
          icon={<MdCalendarToday className="size-5" />}
          containerClassName="h-11 w-full sm:w-56"
          value={mois}
          onChange={(event) => filtrer(setMois)(Number(event.target.value))}
        >
          {FENETRES.map((fenetre) => (
            <option key={fenetre.valeur} value={fenetre.valeur}>
              {fenetre.label}
            </option>
          ))}
        </Select>

        {data ? (
          <p className="text-sm text-content-muted sm:ml-auto">
            Encaissé sur la sélection :{' '}
            <span className="font-semibold text-content">{formatEuros(data.encaisse)}</span>
          </p>
        ) : null}
      </div>

      <DataTable
        mobileCards
        data={data?.items ?? []}
        columns={columns}
        isLoading={isPending}
        skeletonRows={10}
        rowId={(transaction) => transaction.id}
        emptyMessage={
          debouncedSearch ? `Aucun résultat pour « ${debouncedSearch} ».` : 'Aucune transaction.'
        }
      />

      {data ? (
        <Pagination
          page={data.page}
          total={data.total}
          limit={data.limit}
          onChange={setPage}
          label="transactions"
        />
      ) : null}
    </div>
  );
}
